import { io, Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@beshek/shared';
import { useEffect, useState } from 'react';

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: TypedSocket | null = null;

interface ConnectOptions {
  token: string;
  role: 'player' | 'screen';
  roomCode?: string;
}

export function connectSocket(options: ConnectOptions): TypedSocket {
  if (socket?.connected) {
    socket.disconnect();
  }

  const url = import.meta.env.DEV ? 'http://localhost:3000' : window.location.origin;

  socket = io(url, {
    auth: {
      token: options.token,
      role: options.role,
      roomCode: options.roomCode,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 20,
    reconnectionDelayMax: 5000,
    timeout: 60000,
    reconnectionDelay: 1000,
  });

  socket.on('connect', () => {
    console.log('✓ Socket connected:', socket?.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('✗ Socket disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('Socket connection error:', error.message);
  });

  // ============================================
  // 🔄 AUTO-REJOIN AFTER RECONNECT
  // ============================================
  socket.io.on('reconnect', (attemptNumber: number) => {
    console.log('✓ Socket reconnected after', attemptNumber, 'attempts');
    
    // Восстанавливаем комнату если были в ней
    const roomCode = options.roomCode;
    const currentSocket = socket;
    
    if (roomCode && currentSocket) {
      console.log('↻ Rejoining room:', roomCode);
      currentSocket.emit('room:join', { code: roomCode }, (response: any) => {
        if ('room' in response) {
          console.log('✓ Room rejoined:', roomCode);
        } else {
          console.error('✗ Failed to rejoin room:', response.error);
        }
      });
    }
  });

  return socket;
}

export function getSocket(): TypedSocket | null {
  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

// React хук
export function useSocket(): TypedSocket | null {
  const [currentSocket, setCurrentSocket] = useState<TypedSocket | null>(socket);

  useEffect(() => {
    const checkSocket = () => {
      if (socket !== currentSocket) {
        setCurrentSocket(socket);
      }
    };

    // Проверяем при монтировании
    checkSocket();

    // Подписываемся на изменения
    const interval = setInterval(checkSocket, 100);

    return () => clearInterval(interval);
  }, [currentSocket]);

  return currentSocket;
}

// Хук для конкретного события
export function useSocketEvent<K extends keyof ServerToClientEvents>(
  event: K,
  handler: ServerToClientEvents[K]
): void {
  const socket = useSocket();

  useEffect(() => {
    if (!socket) return;

    socket.on(event, handler as any);

    return () => {
      socket.off(event, handler as any);
    };
  }, [socket, event, handler]);
}